import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, Loader2, ShieldCheck, Video } from "lucide-react";

/**
 * Sala de vídeo Jitsi as a Service (JaaS 8x8) — participante entra pelo
 * painel do próprio booking. Backend gera JWT RS256 assinado pra sala
 * `raf-consult-{bookingId}`. Sem watermark, sem limite de 5 minutos.
 *
 * Fallback: se JaaS não configurado (status 503), cai no `meet.jit.si`
 * público antigo pra não bloquear consultas em ambiente de dev.
 */
const ConsultationRoom = () => {
  const { bookingId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const containerRef = useRef(null);
  const apiRef = useRef(null);
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/bookings/${bookingId}`).then(r => setBooking(r.data))
      .catch(e => setErr(e?.response?.data?.detail || "Reserva não encontrada"))
      .finally(() => setLoading(false));
  }, [bookingId]);

  useEffect(() => {
    if (!booking || !containerRef.current) return;
    const roomSlug = booking.jitsi_room || `raf-consult-${bookingId}`;

    const loadScript = (src) => new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${src}"]`);
      if (existing && window.JitsiMeetExternalAPI) return resolve();
      const s = existing ?? document.createElement("script");
      s.src = src;
      s.async = true;
      s.onload = resolve;
      s.onerror = reject;
      if (!existing) document.body.appendChild(s);
    });

    let cancelled = false;
    (async () => {
      let mode = "jaas";
      let jaasData = null;
      // 1) Tenta obter token JaaS
      try {
        const { data } = await api.get(`/consulta/${roomSlug}/jaas-token`);
        jaasData = data;
      } catch (e) {
        // 503 → JaaS não configurado; qualquer outra falha → fallback
        console.warn("JaaS token unavailable, falling back to meet.jit.si:", e?.response?.status);
        mode = "public";
      }

      if (cancelled) return;

      const displayName = user?.role === "profissional"
        ? `Prof. ${(booking.professional_name || user?.name || "").split(" ")[0]}`
        : (user?.name || "Paciente").split(" ")[0];

      if (mode === "jaas" && jaasData) {
        // JaaS: SDK específico do App ID
        const sdkUrl = `https://8x8.vc/${jaasData.app_id}/external_api.js`;
        try { await loadScript(sdkUrl); } catch { setErr("Falha ao carregar SDK JaaS"); return; }
        if (cancelled || !containerRef.current) return;
        apiRef.current = new window.JitsiMeetExternalAPI("8x8.vc", {
          roomName: jaasData.room_full_name,
          jwt: jaasData.token,
          parentNode: containerRef.current,
          userInfo: { displayName },
          configOverwrite: {
            prejoinPageEnabled: true,
            disableDeepLinking: true,
            startWithAudioMuted: false,
            startWithVideoMuted: false,
          },
          interfaceConfigOverwrite: {
            TOOLBAR_BUTTONS: ["microphone","camera","desktop","fullscreen","hangup","chat","tileview","settings","raisehand"],
            SHOW_JITSI_WATERMARK: false,
            SHOW_WATERMARK_FOR_GUESTS: false,
            DEFAULT_BACKGROUND: "#0B2B62",
          },
        });
      } else {
        // Fallback público (dev): meet.jit.si — só 5 min
        try { await loadScript("https://meet.jit.si/external_api.js"); } catch { setErr("Falha ao carregar SDK Jitsi"); return; }
        if (cancelled || !containerRef.current) return;
        apiRef.current = new window.JitsiMeetExternalAPI("meet.jit.si", {
          roomName: roomSlug,
          parentNode: containerRef.current,
          userInfo: { displayName },
          configOverwrite: {
            prejoinPageEnabled: true,
            disableDeepLinking: true,
            startWithAudioMuted: false,
            startWithVideoMuted: false,
          },
          interfaceConfigOverwrite: {
            TOOLBAR_BUTTONS: ["microphone","camera","desktop","fullscreen","hangup","chat","tileview","settings"],
            SHOW_JITSI_WATERMARK: false,
          },
        });
      }

      apiRef.current?.addEventListener?.("readyToClose", () => nav("/"));
    })().catch(() => setErr("Não foi possível carregar a sala de vídeo"));

    return () => {
      cancelled = true;
      if (apiRef.current) { try { apiRef.current.dispose(); } catch {} }
    };
  }, [booking, bookingId, user, nav]);

  if (loading) return <div className="p-10 text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Preparando sala…</div>;
  if (err) return <div className="p-10 text-red-600">{err} — <Link to="/" className="underline">voltar</Link></div>;

  return (
    <div className="min-h-[calc(100vh-80px)] bg-slate-950 text-white" data-testid="consultation-room">
      <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
        <Link to={user?.role === "profissional" ? "/profissional" : "/"}
          className="inline-flex items-center gap-1 text-xs text-slate-300 hover:text-white">
          <ArrowLeft className="w-3.5 h-3.5" /> Sair da sala
        </Link>
        <div className="text-xs text-slate-400 inline-flex items-center gap-1.5">
          <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
          Sala privada RAF · JaaS (8x8) criptografado
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-4 pb-3">
        <div className="mb-2 text-sm text-slate-300 inline-flex items-center gap-1.5">
          <Video className="w-4 h-4 text-emerald-400" />
          Consulta agendada: <b className="text-white ml-1">{new Date(booking?.scheduled_at).toLocaleString("pt-BR")}</b>
        </div>
        <div ref={containerRef} className="w-full h-[70vh] rounded-xl overflow-hidden bg-black" data-testid="jitsi-container" />
        <p className="mt-3 text-xs text-slate-400 text-left">
          Dica: se a câmera/microfone não abrir, permita as permissões no navegador. A sala fica ativa durante toda a janela reservada.
        </p>
      </div>
    </div>
  );
};

export default ConsultationRoom;
